/**
 * ChargerImageLoader Component
 * 
 * Efficiently loads charger images with:
 * - Lazy loading from cache/server
 * - Automatic fallback to static assets
 * - Loading states
 * - Error handling
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Image, ImageStyle, StyleProp, ActivityIndicator, View } from 'react-native';
import { loadChargerThumbnail, invalidateCacheEntry, getCachedImageUri } from '../services/charger-image-cache-service';

interface ChargerImageLoaderProps {
  chargerId: string | undefined;
  style: StyleProp<ImageStyle>;
  fallbackSource: any; // Static asset require()
  resizeMode?: 'cover' | 'contain' | 'stretch' | 'center';
  showLoader?: boolean;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  onError?: (error: Error) => void;
  alt?: string;
  circular?: boolean; // NEW: Apply circular mask
}

/**
 * ChargerImageLoader - Lazy loads charger images with caching and fallback
 * 
 * Usage:
 * ```tsx
 * <ChargerImageLoader
 *   chargerId={charger.uuid}
 *   style={{ width: 50, height: 50 }}
 *   fallbackSource={require('../../assets/chargerStation.png')}
 *   resizeMode="cover"
 *   circular={true}  // Apply circular mask
 * />
 * ```
 */
const ChargerImageLoader: React.FC<ChargerImageLoaderProps> = ({
  chargerId,
  style,
  fallbackSource,
  resizeMode = 'cover',
  showLoader = false,
  onLoadStart,
  onLoadEnd,
  onError,
  alt,
  circular = false,
}) => {
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [retryAttempted, setRetryAttempted] = useState<boolean>(false);

  // Load image when component mounts or chargerId changes
  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      // No charger ID, use fallback
      if (!chargerId) {
        setImageUri(null);
        return;
      }

      setIsLoading(true);
      setHasError(false);
      onLoadStart?.();

      try {
        // Check cache first to load immediately if available
        const cachedUri = await getCachedImageUri(chargerId);
        
        if (cachedUri && isMounted) {
          // Use cached version immediately
          setImageUri(cachedUri);
          setIsLoading(false);
          onLoadEnd?.();
          
          // Verify cached file still exists in background
          // If it fails to load, onError will handle retry
          return;
        }
        
        // No cache or cache miss - load from server
        const uri = await loadChargerThumbnail(chargerId);
        
        if (isMounted) {
          if (uri) {
            setImageUri(uri);
          } else {
            // No custom image found, will use fallback
            setImageUri(null);
          }
        }
      } catch (error) {
        console.error(`Error loading image for charger ${chargerId}:`, error);
        
        if (isMounted) {
          setHasError(true);
          setImageUri(null);
          onError?.(error instanceof Error ? error : new Error(String(error)));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          onLoadEnd?.();
        }
      }
    };

    loadImage();

    // Cleanup
    return () => {
      isMounted = false;
    };
  }, [chargerId, onLoadStart, onLoadEnd, onError]);

  // Handle image load errors (e.g., cached file no longer exists)
  const handleImageError = useCallback(async (e: any) => {
    const errorMessage = e.nativeEvent?.error || '';
    console.error('Image failed to load:', errorMessage);
    
    // Check if this is a file system error (cached file doesn't exist)
    const isFileSystemError = 
      typeof errorMessage === 'string' && 
      (errorMessage.includes("couldn't be opened") || 
       errorMessage.includes("No such file") ||
       errorMessage.includes("file not found"));
    
    // If we have a charger ID and this looks like a stale cache error    
    if (chargerId && imageUri && (isFileSystemError || !retryAttempted)) {
      // Invalidating stale cache and retrying
      setRetryAttempted(true);
      setIsLoading(true);
      
      try {
        // Invalidate all cached entries for this charger (thumbnail and original)
        await invalidateCacheEntry(chargerId);
        await invalidateCacheEntry(`${chargerId}_original`);
        await invalidateCacheEntry(`${chargerId}_medium`);
        
        // Retry loading with force refresh (bypasses cache)
        const uri = await loadChargerThumbnail(chargerId, 'thumbnail', true);
        if (uri) {
          setImageUri(uri);
          setHasError(false);
          setIsLoading(false);
          // Successfully reloaded image
          return;
        }
      } catch (error) {
        // Retry failed, fall back to default
      } finally {
        setIsLoading(false);
      }
    }
    
    // Fall back to static asset
    setHasError(true);
    setImageUri(null);
  }, [chargerId, imageUri, retryAttempted]);

  // Use custom image if available, otherwise fallback to static asset
  const imageSource = imageUri ? { uri: imageUri } : fallbackSource;

  // Apply circular styling if requested
  const imageStyle = circular
    ? [style, { borderRadius: 9999 }] // Very large borderRadius for circular effect
    : style;

  return (
    <View style={style}>
      <Image
        source={imageSource}
        style={imageStyle}
        resizeMode={resizeMode}
        alt={alt}
        onError={handleImageError}
      />
      
      {/* Optional loading indicator */}
      {showLoader && isLoading && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
        >
          <ActivityIndicator size="small" color="#39BEB7" />
        </View>
      )}
    </View>
  );
};

export default ChargerImageLoader;

/**
 * Hook version for more flexibility
 * 
 * Usage:
 * ```tsx
 * const { imageUri, isLoading, hasError } = useChargerImage(charger.uuid);
 * 
 * <Image 
 *   source={imageUri ? { uri: imageUri } : require('../../assets/default.png')} 
 *   style={styles.image}
 * />
 * ```
 */
export const useChargerImage = (chargerId: string | undefined) => {
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const loadImage = useCallback(async (forceRefresh: boolean = false) => {
    if (!chargerId) {
      setImageUri(null);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      const uri = await loadChargerThumbnail(chargerId, 'thumbnail', forceRefresh);
      setImageUri(uri);
    } catch (error) {
      console.error(`Error loading image for charger ${chargerId}:`, error);
      setHasError(true);
      setImageUri(null);
    } finally {
      setIsLoading(false);
    }
  }, [chargerId]);

  useEffect(() => {
    loadImage();
  }, [loadImage]);

  return {
    imageUri,
    isLoading,
    hasError,
    reload: () => loadImage(true),
  };
};

